import { VisitsObjectiveSummary } from "@/services/api/trackReports";
import {
  Bar,
  BarChart,
  Cell,
  LabelList,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
  Legend,
} from "recharts";

interface VisitsObjectivesChartsProps {
  objectives: VisitsObjectiveSummary[];
}

interface CustomObjectivesTooltipProps {
  active?: boolean;
  payload?: Array<{ value: number; dataKey: string }>;
  label?: string;
}

const CustomObjectivesTooltip = ({
  active,
  payload,
  label,
}: CustomObjectivesTooltipProps) => {
  if (!active || !payload?.length || label == null) return null;
  return (
    <div className="bg-white dark:bg-slate-700 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-600 p-2 rounded shadow-md">
      <p className="font-semibold m-0">{`Objetivo: ${label}`}</p>
      <p className="m-0">{`${payload[0].value} visitas`}</p>
    </div>
  );
};

const VisitsObjectivesCharts = ({
  objectives,
}: VisitsObjectivesChartsProps) => {
  const aggregated = objectives.map((objective) => ({
    objectiveId: objective.objective_id,
    objectiveName: objective.objective_title || "Sem objetivo",
    visitsCount: objective.visits_count,
  }));

  if (!aggregated.length) {
    return (
      <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xs border border-slate-100 dark:border-slate-700">
        <p className="text-center text-sm text-slate-600 dark:text-slate-300">
          Nenhuma visita encontrada para exibir objetivos.
        </p>
      </div>
    );
  }

  const colors = [
    "#307089",
    "#68b3d1",
    "#1d4ed8",
    "#0ea5e9",
    "#22c55e",
    "#a855f7",
    "#f97316",
    "#ef4444",
  ];

  const aggregatedWithColors = aggregated.map((item, index) => ({
    ...item,
    color: colors[index % colors.length],
  }));

  const totalVisits = aggregatedWithColors.reduce(
    (sum, item) => sum + item.visitsCount,
    0,
  );

  const barChartData = aggregatedWithColors.map((item) => ({
    label: item.objectiveName,
    count: item.visitsCount,
    color: item.color,
  }));

  const pieChartData = aggregatedWithColors.map((item) => ({
    name: item.objectiveName,
    value: item.visitsCount,
    percentage: totalVisits ? (item.visitsCount / totalVisits) * 100 : 0,
    color: item.color,
  }));

  return (
    <div className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-xs hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 border border-slate-100 dark:border-slate-700">
      <h3 className="text-sm font-semibold text-slate-600 dark:text-slate-300 mb-4">
        Objetivos das visitas
      </h3>

      <div className="grid grid-cols-1 lg:grid-cols-10 gap-6 items-stretch">
        <div className="lg:col-span-7 h-[280px] text-slate-600 dark:text-slate-300">
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={barChartData}
              margin={{ top: 20, right: 20, left: 10, bottom: 40 }}
            >
              <XAxis
                dataKey="label"
                tick={{ fontSize: 10 }}
                tickFormatter={(label: string) =>
                  label.length > 16 ? `${label.substring(0, 16)}...` : label
                }
              />
              <YAxis hide />
              <Tooltip content={<CustomObjectivesTooltip />} />
              <Bar dataKey="count" name="Número de visitas">
                {barChartData.map((entry) => (
                  <Cell key={entry.label} fill={entry.color} />
                ))}
                <LabelList
                  dataKey="count"
                  position="top"
                  className="fill-slate-700 dark:fill-slate-200"
                />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="lg:col-span-3 h-[280px] text-slate-600 dark:text-slate-300">
          <ResponsiveContainer width="100%" height="100%">
            <PieChart>
              <Tooltip
                content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const p = payload[0].payload as {
                    percentage?: number;
                    name?: string;
                  };
                  const value = payload[0].value as number;
                  const percentage = p?.percentage ?? 0;
                  const objectiveName = p?.name ?? "Sem objetivo";

                  return (
                    <div className="bg-white dark:bg-slate-700 text-slate-900 dark:text-white border border-slate-200 dark:border-slate-600 p-2 rounded shadow-md">
                      <p className="font-semibold m-0">{`${objectiveName} (${percentage.toFixed(1)}%)`}</p>
                      <p className="m-0">{`${value} visitas`}</p>
                    </div>
                  );
                }}
              />

              {/* Adicionamos a Legenda na parte inferior */}
              <Legend
                verticalAlign="bottom"
                height={36}
                iconType="circle"
                wrapperStyle={{ fontSize: "12px" }}
              />

              <Pie
                data={pieChartData}
                dataKey="value"
                nameKey="name"
                cx="50%"
                cy="45%" /* Subimos o centro um pouquinho para dar espaço à legenda */
                outerRadius="75%" /* Podemos aumentar a pizza novamente! */
                stroke="none" /* Remove a borda branca se preferir um visual mais flat */
                /* Removemos a prop 'label' completamente */
              >
                {pieChartData.map((entry) => (
                  <Cell key={entry.name} fill={entry.color} />
                ))}
              </Pie>
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
};

export default VisitsObjectivesCharts;
